// The stored jsonb blob on a feed input row. Every percentage is optional and
// arrives from the database as either a number or a numeric string (jsonb round
// trips and hand-entered values both occur), so each field is typed as the union
// numberOrZero already copes with rather than being narrowed to `number`.
export type NutritionSnapshot = {
  moisture_pct?: number | string | null;
  protein_pct?: number | string | null;
  nfc_pct?: number | string | null;
};

export type FeedInput = {
  nutrition_snapshot?: NutritionSnapshot | null;
  lbs_as_fed?: number | string | null;
};

// parseFloat's declared parameter is `string`, but this helper is deliberately
// fed raw row values (numbers, nulls, undefined) and relies on the runtime
// coercion parseFloat performs. String(value) is NOT introduced here: that would
// change behaviour for objects and arrays. The cast keeps the existing call
// exactly as it is and confines the looseness to this one line.
function numberOrZero(value: unknown): number {
  const n = parseFloat(value as string);
  return Number.isFinite(n) ? n : 0;
}

function clampPercent(value: unknown): number {
  const n = numberOrZero(value);
  return Math.max(0, Math.min(100, n));
}

// As-fed pounds for one feed row: the entered quantity in the feed's own unit,
// times that unit's weight. A missing or unparseable unit weight falls back to
// 1, which is what a feed already measured in pounds means, and is the
// behaviour the Add Report save has always had.
//
// This was computed inline and identically in two places (the cattle save and
// the sheep save). The Add Report redesign shows the figure live in the row as
// the operator types, so rather than add a third copy the one derivation lives
// here and all three callers share it — the number on screen is then the same
// number that gets stored, by construction rather than by coincidence.
export function feedAsFedLbs(qty: unknown, unitWeightLbs: unknown): number {
  const q = numberOrZero(qty);
  const w = parseFloat(String(unitWeightLbs));
  return Math.round(q * (Number.isFinite(w) && w !== 0 ? w : 1) * 100) / 100;
}

export function feedDryMatterLbs(lbsAsFed: unknown, nutritionSnapshot: NutritionSnapshot = {}) {
  const lbs = numberOrZero(lbsAsFed);
  const moisturePct = clampPercent(nutritionSnapshot?.moisture_pct);
  return lbs * ((100 - moisturePct) / 100);
}

export function feedNutritionContribution(feed: FeedInput = {}) {
  const nutritionSnapshot = feed.nutrition_snapshot || {};
  const asFedLbs = numberOrZero(feed.lbs_as_fed);
  const dryMatterLbs = feedDryMatterLbs(asFedLbs, nutritionSnapshot);
  return {
    asFedLbs,
    dryMatterLbs,
    crudeProteinLbs: dryMatterLbs * (numberOrZero(nutritionSnapshot.protein_pct) / 100),
    nfcLbs: dryMatterLbs * (numberOrZero(nutritionSnapshot.nfc_pct) / 100),
  };
}
